import { Injectable } from '@angular/core';
import { log, SEVERITY } from '@core-mkt/shared/lib/logger';
import { ServerWistiaService } from './server-wistia.service';

/**
 * Minimal interface for Aurora <wistia-player> popover methods.
 * Aurora does not ship TypeScript types, so we declare only what we use.
 */
interface WistiaPlayerElement extends HTMLElement {
  play(): void;
  muted: boolean;
  volume: number;
}

@Injectable({
  providedIn: 'root',
})
export class WistiaService extends ServerWistiaService {
  private ctaListenerAttached = false;
  /**
   * The CTA with a load in flight, and the callback that settles it. Single-slot,
   * because `loadVideoScripts` keeps exactly one popover player and removes any
   * previous one — so a second CTA's load destroys the first CTA's player, and its
   * `popover-show` can never fire. Adopting a new trigger therefore has to settle
   * the old one, or it would sit busy and unclickable until the timeout.
   *
   * Kept on the instance rather than read back off the element's attribute so the
   * guard is owned by the one service that registered the listener — a second
   * WistiaService (only ever the case under test) can't trip this one.
   */
  private ctaLoadingTrigger?: Element;
  private ctaLoadingClear?: () => void;

  /**
   * Wires the Vision CTA "Wistia Trigger" button (EPS-4629; authored via the
   * EPS-4578 CKEditor plugin). The button is emitted as raw redactor HTML and can
   * appear inside any WYSIWYG wrapper, so instead of per-component handlers we use
   * a single delegated click listener on the document. The button carries all it
   * needs in the DOM (`data-wistia-id`) — no out-of-band data — so delegation is
   * sufficient. A click opens the shared Wistia popover via loadVideoScripts.
   *
   * The marker class `wistia-cta-trigger` is on both the <button> and its label
   * <span>; `data-wistia-id` lives only on the button, so `closest()` resolves the
   * button whether the click lands on the button or the label. Idempotent — the
   * listener is attached at most once.
   */
  override initializeCtaTriggers = () => {
    if (typeof window === 'undefined' || this.ctaListenerAttached) return;
    this.ctaListenerAttached = true;

    this.document.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement | null;
      const trigger = target?.closest?.('.wistia-cta-trigger[data-wistia-id]');
      const wistiaId = trigger?.getAttribute('data-wistia-id');
      if (!wistiaId) return;

      // The id is hand-typed into CKEditor, so a pasted URL or an id containing
      // `-`/`_` reaches us looking valid. loadVideoScripts would return silently at
      // its isValidWistiaId guard and the button would just do nothing — surface it
      // so the content author can see why.
      if (!this.isValidWistiaId(wistiaId)) {
        log({
          severity: SEVERITY.WARN,
          code: 40405,
          message: 'Invalid Wistia ID on CTA trigger',
          contextId: wistiaId,
          tags: ['WistiaService.initializeCtaTriggers'],
        });
        return;
      }

      this.openCtaPopover(trigger, wistiaId);
    });
  };

  /**
   * Opens the shared Wistia popover from a CTA element, with the busy state and
   * repeat-click guard. Used by the delegated CKEditor trigger above and by
   * component CTAs (e.g. the homepage hero's video button).
   *
   * Scripts take a beat to land and these CTAs render on pages that have no
   * <vision-video-button>, so #video-cta-spinner does not exist there and
   * showSpinner() no-ops. Without feedback a click looks dead and invites a
   * second one, and every click tears down and recreates the popover player.
   */
  override openCtaPopover = (trigger: Element, wistiaId: string) => {
    if (this.ctaLoadingTrigger === trigger) return;
    this.markCtaLoading(trigger, wistiaId);

    this.loadVideoScripts(wistiaId, true);
  };

  /**
   * Marks a CTA as loading until its popover opens, so the button can show a
   * pressed/busy state on pages without the shared #video-cta-spinner overlay.
   * Cleared on the first popover-show, or after a timeout so a failed script
   * load can't leave the button permanently inert.
   *
   * Matched on `media-id` so a different CTA's popover opening mid-load cannot
   * clear this one's busy state early and re-open the door to a repeat click.
   */
  private markCtaLoading(trigger: Element, wistiaId: string): void {
    const CTA_LOADING_TIMEOUT_MS = 10000;
    // Settle whichever CTA was loading before — its player is about to be removed.
    this.ctaLoadingClear?.();
    this.ctaLoadingTrigger = trigger;
    trigger.setAttribute('data-wistia-loading', '');
    trigger.setAttribute('aria-busy', 'true');

    let settled = false;
    const clear = () => {
      if (settled) return;
      settled = true;
      if (this.ctaLoadingTrigger === trigger) {
        this.ctaLoadingTrigger = undefined;
        this.ctaLoadingClear = undefined;
      }
      observer.disconnect();
      clearTimeout(timeoutId);
      trigger.removeAttribute('data-wistia-loading');
      trigger.removeAttribute('aria-busy');
    };

    this.ctaLoadingClear = clear;
    const timeoutId = setTimeout(clear, CTA_LOADING_TIMEOUT_MS);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (
            node instanceof HTMLElement &&
            node.tagName === 'WISTIA-PLAYER' &&
            node.hasAttribute('wistia-popover') &&
            node.getAttribute('media-id') === wistiaId
          ) {
            // Stop observing once the player is found — `clear()` disconnects too, but
            // that can be up to 10s away, and this matches hideSpinnerOnVideoReady.
            observer.disconnect();
            node.addEventListener('popover-show', clear, { once: true });
            return;
          }
        }
      }
    });
    // Direct children only: loadVideoScripts appends the popover player straight to
    // document.body. If that ever moves into a container, this needs `subtree: true`.
    observer.observe(this.document.body, { childList: true });
  }

  /**
   * Defers Wistia script loading until the player scrolls into view (inline/embed)
   * or is clicked (popover). Keeps page-load Wistia footprint to just swatch images (~4 KB each).
   * The swatch CSS placeholder uses :not(:defined) and renders without any JS.
   */
  override initializePopovers = () => {
    if (typeof window === 'undefined') return;

    // Autoplay videos are deferred divs (NOT <wistia-player>) to prevent
    // player.js from auto-discovering them and poisoning global muted state.
    // Create the real <wistia-player autoplay muted> when scrolled into view.
    // Exclude the Wistia Trigger CTA (EPS-4629): it reuses `data-wistia-autoplay`
    // as a boolean flag ("true"/"false") rather than a media id, and both literals
    // pass isValidWistiaId — so without this guard the CTA button would be replaced
    // by a broken autoplay player. The CTA is handled by initializeCtaTriggers().
    const autoplayDivs = this.document.querySelectorAll<HTMLElement>('[data-wistia-autoplay]:not(.wistia-cta-trigger)');
    if (autoplayDivs.length) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const div = entry.target as HTMLElement;
            const mediaId = div.getAttribute('data-wistia-autoplay');
            if (!mediaId || !this.isValidWistiaId(mediaId)) return;
            observer.unobserve(div);

            this.insertWistiaPlayerScript();
            this.insertWistiaEmbedScript(mediaId);

            const player = this.document.createElement('wistia-player');
            player.setAttribute('media-id', mediaId);
            player.setAttribute('autoplay', '');
            player.setAttribute('muted', '');
            player.setAttribute('silent-auto-play', 'allow');
            player.setAttribute('resumable', 'false');
            if (div.hasAttribute('data-wistia-loop')) {
              player.setAttribute('end-video-behavior', 'loop');
            }
            player.style.width = '100%';
            player.style.display = 'block';
            div.replaceWith(player);
          });
        },
        { rootMargin: '200px' },
      );
      autoplayDivs.forEach((div) => observer.observe(div));
    }

    // All non-autoplay videos (popovers + inline embeds) are static poster divs.
    // Upgrade thumbnails, show duration on hover, load scripts on click.
    const posters = this.document.querySelectorAll<HTMLElement>('[data-wistia-popover], [data-wistia-poster]');
    posters.forEach((trigger) => {
      const isPopover = trigger.hasAttribute('data-wistia-popover');
      const wistiaId = trigger.getAttribute(isPopover ? 'data-wistia-popover' : 'data-wistia-poster');
      if (!wistiaId) return;

      // Shared: upgrade swatch to sharp thumbnail + set duration
      this.setupPoster(trigger, wistiaId);

      if (isPopover) {
        trigger.addEventListener('click', () => {
          this.loadVideoScripts(wistiaId, true);
        });
      } else {
        trigger.addEventListener(
          'click',
          () => {
            this.replacePosterWithPlayer(trigger, wistiaId);
          },
          { once: true },
        );
      }
    });
  };

  /**
   * Loads Wistia scripts and opens a popover video.
   * Aurora requires both player.js AND the media-specific embed script.
   * Creates a hidden popover player with popover-content="html" (no thumbnail)
   * and uses the popover-show-on-load attribute so Aurora opens it automatically
   * once scripts finish loading.
   *
   * Manages the video-cta-spinner lifecycle internally:
   * - First open: shows spinner, hides it when popover-show fires (or after timeout)
   * - Re-open: no spinner needed since the player is already initialized
   */
  loadVideoScripts = (wistiaId: string, userInitiated: boolean = false) => {
    this.insertWistiaPlayerScript();

    if (!userInitiated || !this.isValidWistiaId(wistiaId)) {
      return;
    }

    this.insertWistiaEmbedScript(wistiaId);

    // Remove any existing popover player to prevent multi-video conflicts.
    // Each click gets a clean player. Inline embed players are not affected.
    this.document.querySelectorAll('body > wistia-player[wistia-popover]').forEach((el) => el.remove());

    this.showSpinner();
    this.hideSpinnerOnVideoReady(wistiaId);

    // No `autoplay` attribute — it triggers browser autoplay policy which forces
    // muted, and Wistia persists that muted state globally across all player
    // instances, causing subsequent inline embeds to start muted.
    // popover-show-on-load opens the overlay; we manually play on api-ready.
    const player = this.document.createElement('wistia-player');
    player.setAttribute('media-id', wistiaId);
    player.setAttribute('wistia-popover', '');
    player.setAttribute('popover-content', 'html');
    player.setAttribute('popover-show-on-load', '');
    player.setAttribute('resumable', 'false');
    this.document.body.appendChild(player);

    // Play once Aurora is ready. No autoplay means no browser-forced mute.
    // The user clicked to open this, so we have user activation to allow audio.
    player.addEventListener(
      'api-ready',
      () => {
        const p = player as unknown as WistiaPlayerElement;
        p.muted = false;
        p.play();
      },
      { once: true },
    );
  };

  /**
   * Overlays a real <wistia-player> on top of the poster for inline embeds.
   * The poster stays in place (holding layout), the player loads on top of it,
   * so there is zero layout shift. Once Aurora is playing, the poster is removed.
   */
  private replacePosterWithPlayer(poster: HTMLElement, wistiaId: string): void {
    this.insertWistiaPlayerScript();
    this.insertWistiaEmbedScript(wistiaId);

    // Scope styles to THIS poster only — other posters with the same wistiaId stay untouched
    const scopeClass = `wistia-playing-${Math.random().toString(36).slice(2, 8)}`;
    poster.classList.add(scopeClass);

    // Hide play button pseudo-elements and duration, show a loading spinner
    const hideStyle = this.document.createElement('style');
    hideStyle.textContent =
      `.${scopeClass}::before,.${scopeClass}::after{display:none!important}` +
      `.${scopeClass}{cursor:default}` +
      `.${scopeClass} .wistia-duration{display:none!important}` +
      `.${scopeClass} .wistia-embed-spinner{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;z-index:3}` +
      `.${scopeClass} .wistia-embed-spinner>div{width:40px;height:40px;border:4px solid rgba(255,255,255,0.3);border-top-color:white;border-radius:50%;animation:wistia-spin 0.8s linear infinite}` +
      `@keyframes wistia-spin{to{transform:rotate(360deg)}}`;
    this.document.head.appendChild(hideStyle);

    // Add a loading spinner on top of the poster thumbnail
    const spinner = this.document.createElement('div');
    spinner.className = 'wistia-embed-spinner';
    spinner.innerHTML = '<div></div>';
    poster.appendChild(spinner);

    // Clip the poster so the player can't visually leak below during script loading
    poster.style.overflow = 'hidden';

    // Overlay the player absolutely on top of the poster — zero layout shift.
    // `resumable="false"` prevents Wistia from restoring persisted state
    // (e.g. muted from a prior popover autoplay session).
    const player = this.document.createElement('wistia-player');
    player.setAttribute('media-id', wistiaId);
    player.setAttribute('resumable', 'false');
    player.style.position = 'absolute';
    player.style.inset = '0';
    player.style.width = '100%';
    player.style.height = '100%';
    player.style.display = 'block';
    player.style.zIndex = '4';
    poster.appendChild(player);

    // Remove spinner once the video starts playing.
    player.addEventListener('play', () => spinner.remove(), { once: true });

    // Play once Aurora finishes initializing this player.
    player.addEventListener(
      'api-ready',
      () => {
        const p = player as unknown as WistiaPlayerElement;
        p.muted = false;
        p.play();
      },
      { once: true },
    );
  }

  /**
   * Shows the video CTA spinner overlay. Visibility is entirely CSS — the attribute
   * is the only state, and `.spinner-body` owns the fade in both directions.
   */
  private showSpinner(): void {
    this.document.getElementById('video-cta-spinner')?.setAttribute('data-spinner-visible', '');
  }

  /**
   * Fades the overlay back out. Called on `popover-show`, which is the same moment
   * Wistia starts fading in its own identical 50%-black scrim, so the two cross-fade
   * rather than handing off through a bright gap (EPS-4700).
   */
  private hideSpinner(): void {
    this.document.getElementById('video-cta-spinner')?.removeAttribute('data-spinner-visible');
  }

  /**
   * Hides the video CTA spinner once the Aurora popover opens,
   * with a 10-second timeout fallback for network/script failures.
   *
   * Uses a MutationObserver to catch the player element when it's
   * appended to the DOM after this method is called (the normal flow).
   * A `handled` guard ensures the spinner is hidden at most once.
   */
  hideSpinnerOnVideoReady(wistiaId: string): void {
    if (typeof window === 'undefined') return;

    const SPINNER_HIDE_TIMEOUT_MS = 10000;
    let handled = false;
    let observer: MutationObserver | undefined;

    const settle = () => {
      if (handled) return;
      handled = true;
      observer?.disconnect();
      this.hideSpinner();
    };

    if (!wistiaId) {
      settle();
      return;
    }

    const timeoutId = setTimeout(settle, SPINNER_HIDE_TIMEOUT_MS);

    const listenForPopover = (player: Element) => {
      player.addEventListener(
        'popover-show',
        () => {
          clearTimeout(timeoutId);
          settle();
        },
        { once: true },
      );
    };

    // Look specifically for a popover player — autoplay players with the same
    // media-id must be ignored since they never fire popover-show.
    const popoverSelector = `wistia-player[wistia-popover][media-id="${wistiaId}"]`;
    const existing = this.document.querySelector(popoverSelector);

    if (existing) {
      listenForPopover(existing);
    } else {
      // Player will be appended after this call — watch for it
      observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of Array.from(mutation.addedNodes)) {
            if (
              node instanceof HTMLElement &&
              node.tagName === 'WISTIA-PLAYER' &&
              node.hasAttribute('wistia-popover') &&
              node.getAttribute('media-id') === wistiaId
            ) {
              observer?.disconnect();
              listenForPopover(node);
              return;
            }
          }
        }
      });
      // Direct children only: loadVideoScripts appends the popover player straight to
      // document.body. If that ever moves into a container, this needs `subtree: true`.
      observer.observe(this.document.body, { childList: true });
    }
  }
}
